import { Routes } from '@angular/router';
import { EulaComponent } from './components/eula/eula/eula.component';
import { HomeComponent } from './components/home/home.component';
import { NoNavigationLayoutComponent } from './components/no-navigation-layout/no-navigation-layout.component';
import { StandardLayoutComponent } from './components/standard-layout/standard-layout.component';
import { CustomCultureMatcher } from './culture-matcher';
import { LoginCallbackComponent } from './login-callback/login-callback.component';
import { epiGarageRedirectGuard } from './state/user-auth/epi-garage-redirect.guard';
import { epiHomeRedirectGuard } from './state/user-auth/epi-home-redirect.guard';
import { epiRedirectGuard } from './state/user-auth/epi-redirect.guard';
import { eulaAcceptedGuard } from './state/user-auth/eula-accepted.guard';
import { lowerEnvironmentGuard } from './state/user-auth/lower-environment.guard';
import { rideCommandAuthGuard } from './state/user-auth/ride-command-auth.guard';

export const routes: Routes = [
    {
        children: [
            {
                // Root redirect should remain first in list
                path: '',
                pathMatch: 'full',
                redirectTo: '/home',
            },
            {
                children: [
                    {
                        loadChildren: () =>
                            import(
                                './components/policies/privacy-policy/privacy-policy.module'
                            ).then((m) => m.PrivacyPolicyModule),
                        path: 'privacy/standalone',
                    },
                ],
                component: NoNavigationLayoutComponent,
                path: '',
            },
            {
                children: [
                    {
                        component: EulaComponent,
                        data: { title: $localize`End User License Agreement` },
                        path: 'eula',
                    },
                    {
                        data: { title: $localize`Trail News` },
                        loadComponent: () =>
                            import(
                                './components/trail-news/trail-news.component'
                            ).then((c) => c.TrailNewsComponent),
                        path: 'trail-news',
                    },
                ],
                component: StandardLayoutComponent,
                path: '',
            },
            {
                canActivateChild: [eulaAcceptedGuard],
                children: [
                    {
                        loadChildren: () =>
                            import('./components/map/map.routes'),
                        path: 'map',
                    },
                    {
                        loadChildren: () =>
                            import('./components/map-edit/map-edit.routes'),
                        path: 'map-edit',
                    },
                    {
                        canActivate: [epiHomeRedirectGuard],
                        component: HomeComponent,
                        data: {
                            nonBlockingGuards: true,
                            title: $localize`Home`,
                        },
                        path: 'home',
                    },
                    {
                        loadChildren: () =>
                            import(
                                './components/check-in-contacts/check-in-contacts.routes'
                            ),
                        path: 'check-in-contacts',
                    },
                    {
                        loadChildren: () =>
                            import('./components/community/community.routes'),
                        path: 'community',
                    },
                    {
                        canActivateChild: [epiRedirectGuard],
                        loadChildren: () =>
                            import(
                                './components/policies/privacy-policy/privacy-policy.module'
                            ).then((m) => m.PrivacyPolicyModule),
                        path: 'community/policy',
                    },
                    {
                        canActivateChild: [epiRedirectGuard],
                        loadChildren: () =>
                            import(
                                './components/policies/privacy-policy/privacy-policy.module'
                            ).then((m) => m.PrivacyPolicyModule),
                        path: 'privacy',
                    },
                    {
                        loadChildren: () =>
                            import(
                                './components/community/activity/activity.routes'
                            ),
                        path: 'community/activity',
                    },
                    {
                        loadChildren: () =>
                            import(
                                './components/community/community-place/community-place.routes'
                            ),
                        path: 'community/place',
                    },
                    {
                        loadChildren: () =>
                            import(
                                './components/community/ride/community-ride.routes'
                            ),
                        path: 'community/ride',
                    },
                    {
                        loadChildren: () =>
                            import(
                                './components/community/ride-event/ride-event.routes'
                            ),
                        path: 'community/public-ride-event',
                    },
                    {
                        canActivateChild: [rideCommandAuthGuard],
                        loadChildren: () =>
                            import(
                                './components/community/community-favorite/community-favorite.routes'
                            ),
                        path: 'community/saved',
                    },
                    {
                        canActivateChild: [rideCommandAuthGuard],
                        loadChildren: () =>
                            import(
                                './components/community/my-event/my-event.routes'
                            ),
                        path: 'community/my-event',
                    },
                    {
                        canActivateChild: [rideCommandAuthGuard],
                        loadChildren: () =>
                            import('./components/account/account.routes'),
                        path: 'account',
                    },
                    {
                        data: { title: $localize`Admin` },
                        loadComponent: () =>
                            import(
                                './components/admin/client-admin.component'
                            ).then((m) => m.ClientAdminComponent),
                        path: 'admin',
                        pathMatch: 'full',
                    },
                    {
                        canActivateChild: [lowerEnvironmentGuard],
                        data: { title: 'Debug' },
                        loadComponent: () =>
                            import('./components/debug/debug.component').then(
                                (m) => m.DebugComponent
                            ),
                        path: 'debug',
                    },
                    {
                        loadChildren: () =>
                            import(
                                './components/rides-places/shared-rides-places/shared-rides-places.routes'
                            ),
                        path: 'rides/shared-item',
                    },
                    {
                        loadComponent: () =>
                            import(
                                './components/landing-pages/landing-pages.component'
                            ).then((m) => m.LandingPagesComponent),
                        path: 'landing-pages/:landingPageId',
                    },
                    {
                        data: { title: $localize`Not Found` },
                        loadComponent: () =>
                            import(
                                './components/share-unavailable/share-unavailable.component'
                            ).then((m) => m.ShareUnavailableComponent),
                        path: 'share-unavailable',
                        pathMatch: 'full',
                    },
                    {
                        data: { title: $localize`Update` },
                        loadComponent: () =>
                            import(
                                './components/ota-update/ota-update.component'
                            ).then((m) => m.OTAUpdateComponent),
                        path: 'ota-update/:otaId',
                    },
                    {
                        canActivateChild: [epiRedirectGuard],
                        data: { title: $localize`Help & Customer Support` },
                        loadComponent: () =>
                            import(
                                './components/support/support.component'
                            ).then((m) => m.SupportComponent),
                        path: 'support',
                    },
                    {
                        data: { title: $localize`Supported Devices` },
                        loadComponent: () =>
                            import(
                                './components/supported-devices/supported-devices.component'
                            ).then((m) => m.SupportedDevicesComponent),
                        path: 'supported-devices',
                        pathMatch: 'full',
                    },
                    {
                        loadChildren: () =>
                            import(
                                './components/rides-places/rides-places.routes'
                            ),
                        path: 'rides',
                    },
                    {
                        path: 'ridecommand',
                        pathMatch: 'full',
                        redirectTo: 'display',
                    },
                    {
                        path: 'ridecommand/tutorials',
                        pathMatch: 'full',
                        redirectTo: 'display/tutorials',
                    },
                    {
                        path: 'tutorials',
                        pathMatch: 'full',
                        redirectTo: 'display/tutorials',
                    },
                    {
                        path: 'pidd',
                        pathMatch: 'full',
                        redirectTo: 'display',
                    },
                    {
                        path: 'update',
                        pathMatch: 'full',
                        redirectTo: 'display',
                    },
                    {
                        data: { title: $localize`PIDD FAQ's - Display Update` },
                        loadComponent: () =>
                            import(
                                './components/maintenance/display/update/pidd-43/faqs-pidd/faqs-pidd.component'
                            ).then((m) => m.FaqsPiddComponent),
                        path: 'pidd/faqs',
                    },
                    {
                        loadChildren: () =>
                            import(
                                './components/maintenance/display/update/display.routes'
                            ),
                        path: 'display',
                    },
                    {
                        canActivateChild: [epiGarageRedirectGuard],
                        loadChildren: () =>
                            import(
                                './components/maintenance/maintenance.routes'
                            ),
                        path: 'maintenance',
                    },
                    {
                        component: LoginCallbackComponent,
                        path: 'login-callback',
                    },
                    {
                        // Fallback route must remain last in the list
                        data: { title: '404' },
                        loadComponent: () =>
                            import(
                                './components/page-not-found/page-not-found.component'
                            ).then((m) => m.PageNotFoundComponent),
                        path: '**',
                    },
                ],
                component: StandardLayoutComponent,
                path: '',
            },
        ],
        matcher: CustomCultureMatcher.matcher,
    },
];
